import type {
  createArtifactLibrary,
  LibraryInput,
  LibraryItem,
} from '../../../../src/artifacts/lifecycle/library.mjs';
import type { LibraryKind, LibraryKindFilter, LibraryPanelData } from './panels.ts';
import { getArtifactType, type ArtifactRegistry } from '../../../../src/artifacts/registry.mjs';
import { assertLibraryGroups } from './library-groups.ts';

export type LibraryPage = Awaited<
  ReturnType<ReturnType<typeof createArtifactLibrary>['listLibrary']>
>;
export type LibraryControls = Pick<LibraryPanelData, 'segment' | 'query' | 'kind'>;
export type LibraryKindGroups = Readonly<
  Record<Exclude<LibraryKindFilter, 'apps'>, readonly LibraryKind[]> & {
    /** Present only when the registered App descriptor is (PR2). */
    apps?: readonly LibraryKind[];
  }
>;
export type LibraryOpenTarget = {
  artifactId: string;
  versionId: string;
  revisionId: string;
  versionNumber: number;
  purpose: LibraryItem['purpose'];
};
export type LibraryDisplayFacts = Pick<
  LibraryPanelData['items'][number],
  'readOnly' | 'liveBound' | 'valueAsOf'
>;

/** Group membership comes from the accepted kind-filter configuration. SHARE
 * performs current authorization, filtering, counts and cursor verification.
 */
export function libraryQuery(
  controls: LibraryControls,
  groups: LibraryKindGroups,
  cursor: string | null = null,
  registry?: ArtifactRegistry,
): LibraryInput {
  assertLibraryGroups(groups, registry);
  const kinds = groups[controls.kind];
  if (!kinds?.length || new Set(kinds).size !== kinds.length)
    throw new TypeError('library_kind_group_invalid');
  return {
    segment: controls.segment,
    query: controls.query,
    kinds: [...kinds],
    limit: 20,
    ...(cursor === null ? {} : { cursor }),
  };
}

function displayKind(value: string, groups: LibraryKindGroups): LibraryKind {
  const kind = groups.all.find((candidate) => candidate === value);
  if (!kind) throw new TypeError('library_kind_unregistered');
  return kind;
}

/** Only the current authorized SHARE page is accepted here. The host resolves
 * thumbnails for the same original version/audience; missing images stay null.
 * Open targets retain immutable identity outside the row's display labels.
 */
export function projectLibraryPage(
  controls: LibraryControls,
  page: LibraryPage,
  options: {
    groups: LibraryKindGroups;
    /** The host's registry when it carries the registered App descriptor (PR2). */
    registry?: ArtifactRegistry;
    thumbnailUrlFor: (item: Readonly<LibraryItem>) => string | null;
    /** Host-owned facts read for this exact authorized version; absent facts remain unknown. */
    displayFactsFor?: (item: Readonly<LibraryItem>) => LibraryDisplayFacts;
  },
) {
  assertLibraryGroups(options.groups, options.registry);
  const openTargets = new Map<string, LibraryOpenTarget>();
  const items = page.items.map((item) => {
    if (openTargets.has(item.artifactId)) throw new TypeError('library_duplicate_identity');
    if (item.purpose !== 'viewer' && item.purpose !== 'editor')
      throw new TypeError('library_purpose_unavailable');
    openTargets.set(item.artifactId, {
      artifactId: item.artifactId,
      versionId: item.versionId,
      revisionId: item.revisionId,
      versionNumber: item.versionNumber,
      purpose: item.purpose,
    });
    const facts = options.displayFactsFor?.(item);
    // A deck built like an App reads as a deck: its label, icon and exports are a deck's.
    const kind = displayKind(item.presentation ?? item.kind, options.groups);
    return {
      id: item.artifactId,
      title: item.title,
      kind,
      ownerLabel: item.ownerLabel,
      updatedAt: item.updatedAt,
      publish: item.publish,
      publishedNumber: item.publishedNumber,
      audienceLabel: item.audienceLabel,
      madeByAgent: item.madeByAgent,
      refresh: item.refresh === 'none' ? null : item.refresh,
      refreshFailedAt: item.refreshFailedAt,
      canRetryRefresh: item.canRetryRefresh && item.capabilities.refresh,
      thumbnailUrl: item.thumbnailRef === null ? null : options.thumbnailUrlFor(item),
      // Only the formats the registered descriptor states; an App with none shows no export line.
      exportFormats: [
        ...((options.registry ? options.registry.get(kind) : getArtifactType(kind)).exportFormats ??
          []),
      ],
      readOnly: facts?.readOnly ?? null,
      liveBound: facts?.liveBound ?? null,
      valueAsOf: facts?.valueAsOf ?? null,
    };
  });
  const data: LibraryPanelData = {
    ...controls,
    load: 'ready',
    items,
    hasMore: page.nextCursor !== null,
  };
  return {
    data,
    openTargets,
    nextCursor: page.nextCursor,
    total: page.total,
    catalogueGeneration: page.catalogueGeneration,
  };
}
